import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import type { PersonPool, PosAnchorKind, ScopeKind, UserRole } from "@tilt/admin-sdk";

import { ModalOverlay } from "./ModalOverlay";
import { RoleSelect } from "./RoleSelect";
import { WorkplacePicker, type WorkplacePickerValue } from "./WorkplacePicker";
import { EMPTY_WORKPLACE } from "./workplace-picker-constants";

/** What the parent locks the dialog to. When supplied, the workplace
 *  pickers are hidden and replaced with a frozen badge - the new
 *  person's anchor + initial-role scope are forced to this preset.
 *
 *  Used by Partner / Org / Location detail "People" tabs where the
 *  surrounding context already names the workplace, so re-asking the
 *  operator to pick it would just be friction. */
export interface InvitePresetScope {
  readonly scope_kind: "partner" | "organization" | "location";
  readonly scope_id: string;
  readonly partner_id: string;
  readonly partner_path: string;
  /** Display label for the frozen badge ("Honda Group", "Frank Leta
   *  Honda North", etc.). */
  readonly label: string;
  /** Optional - needed when scope_kind === "location" so the POS-pool
   *  flow can anchor at the parent organization (locations aren't
   *  valid POS anchors per V0042). When omitted on a location preset,
   *  the dialog fetches the location row to recover it. */
  readonly organization_id?: string;
}

/**
 * Unified "Add a person" dialog. One entry-point for both populations:
 *
 *   - Admin pool (admin console) → POST /admin/v1/users with optional
 *     ``initial_role``. Lands a row in user_tbl + Cognito invite to
 *     the admin pool.
 *   - POS pool (POS application) → POST /admin/v1/pos-users with the
 *     anchor + optional ``initial_membership``. Lands a row in
 *     pos_user_tbl + Cognito invite to the POS pool.
 *
 * Three top-level modes:
 *
 *   1. **Preset scope** (Partner / Org / Location detail tab) - the
 *      anchor + role-scope are locked to the preset. Operator only
 *      picks pool, profile, and role slug.
 *
 *   2. **Tier = platform** (super_admin only, no preset) - the dialog
 *      auto-resolves the synthetic platform partner and forces
 *      ``role = super_admin``. Platform is treated as the special
 *      root it is, NOT a normal workplace option.
 *
 *   3. **Tier = partner** (default) - the operator picks the partner
 *      OR organization the new person is anchored to, then optionally
 *      grants an initial role at any sub-scope.
 *
 * "Anchor" = where the person LIVES in the hierarchy. Admin pool: a
 * partner (becomes primary_partner). POS pool: a partner OR an
 * organization (the pos_user_tbl.anchor_*); locations are never
 * anchors per V0042 - to put someone at a single store you anchor at
 * the org and grant a location-scope role.
 *
 * "Role applies at" (only when 'Grant an initial role' is checked) =
 * the membership scope inside the anchor's subtree. Defaults to the
 * anchor itself; users opt to deepen to a sub-org or a single
 * location. RoleSelect filters to roles whose permission bundle
 * covers the picked scope kind.
 */

type InviteTier = "partner" | "platform";

export function InvitePersonDialog({
  onClose,
  onCreated,
  callerIsSuperAdmin = false,
  presetScope,
}: {
  readonly onClose: () => void;
  readonly onCreated: () => void;
  /** Show the platform-tier toggle. Default false. The API rejects
   *  the grant either way; this just makes the platform path
   *  reachable in the UI for super-admin callers. Ignored when
   *  ``presetScope`` is set - preset scope is always inside a real
   *  partner subtree. */
  readonly callerIsSuperAdmin?: boolean;
  /** When set, lock both pickers to this scope and hide them. The
   *  new person's anchor is forced to the preset's partner (or
   *  organization, when the preset is org-scoped); the initial role
   *  is forced to the preset's exact scope. */
  readonly presetScope?: InvitePresetScope;
}) {
  const qc = useQueryClient();

  // Default to POS pool when the preset is org- or location-scoped -
  // those pages are almost exclusively used to invite cashiers.
  const [pool, setPool] = useState<PersonPool>(
    presetScope && presetScope.scope_kind !== "partner" ? "pos" : "admin",
  );
  const [tier, setTier] = useState<InviteTier>("partner");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [anchor, setAnchor] = useState<WorkplacePickerValue>(EMPTY_WORKPLACE);
  const [grantRole, setGrantRole] = useState(true);
  const [roleScope, setRoleScope] = useState<WorkplacePickerValue>(EMPTY_WORKPLACE);
  const [roleSlug, setRoleSlug] = useState("");

  // Resolve the platform partner row only when the operator picks
  // platform tier. Without a preset and without platform tier we
  // never need this - saves a request on every dialog open.
  const platformQuery = useQuery({
    queryKey: ["partners", "platform-row"],
    queryFn: () => apiClient.listPartners({ partner_type: "platform", limit: 1 }),
    enabled: !presetScope && callerIsSuperAdmin && tier === "platform",
    staleTime: 5 * 60 * 1000,
  });
  const platformPartner = platformQuery.data?.partners?.[0];

  // Location preset: the POS-pool flow needs the parent organization
  // id to anchor at (locations aren't valid POS anchors). If the
  // caller didn't pass it, fetch the row.
  const locationLookup = useQuery({
    queryKey: ["locations", "preset", presetScope?.scope_id],
    queryFn: () => apiClient.getLocation(presetScope!.scope_id),
    enabled: !!presetScope && presetScope.scope_kind === "location" && !presetScope.organization_id,
  });
  const presetOrganizationId = presetScope?.organization_id ?? locationLookup.data?.organization_id;

  // Anchor depth allowed by the surrounding mode.
  //   * preset scope_kind=partner       → admin: [partner], pos: [partner]
  //   * preset scope_kind=organization  → admin: [partner], pos: [organization]
  //   * preset scope_kind=location      → admin: [partner], pos: [organization]
  //   * platform tier                   → admin: [partner] (the synthetic platform row)
  //   * default partner tier            → admin: [partner], pos: [partner, organization]
  const anchorAllowedKinds: readonly ScopeKind[] = useMemo(() => {
    if (presetScope) {
      if (pool === "admin") return ["partner"] as const;
      if (presetScope.scope_kind === "partner") return ["partner"] as const;
      return ["organization"] as const;
    }
    if (tier === "platform") return ["partner"] as const;
    return pool === "admin" ? (["partner"] as const) : (["partner", "organization"] as const);
  }, [pool, tier, presetScope]);

  // Effective anchor - derived from preset / platform tier / picker
  const effectiveAnchor: WorkplacePickerValue = useMemo(() => {
    if (presetScope) {
      if (pool === "admin") {
        return {
          kind: "partner",
          id: presetScope.partner_id,
          labels: {
            partner: presetScope.partner_path.split(".").pop() ?? "partner",
            organization: null,
            location: null,
          },
        };
      }
      // POS pool: anchor at the partner if preset is partner-scoped,
      // otherwise at the organization (preset.scope_id for org,
      // resolved parent org for location).
      if (presetScope.scope_kind === "partner") {
        return {
          kind: "partner",
          id: presetScope.partner_id,
          labels: {
            partner: presetScope.label,
            organization: null,
            location: null,
          },
        };
      }
      const orgId =
        presetScope.scope_kind === "organization"
          ? presetScope.scope_id
          : (presetOrganizationId ?? "");
      return {
        kind: "organization",
        id: orgId,
        labels: {
          partner: null,
          organization:
            presetScope.scope_kind === "organization" ? presetScope.label : "organization",
          location: null,
        },
      };
    }
    if (tier === "platform") {
      return {
        kind: "partner",
        id: platformPartner?.id ?? "",
        labels: { partner: "Platform", organization: null, location: null },
      };
    }
    return anchor;
  }, [presetScope, pool, tier, platformPartner, anchor, presetOrganizationId]);

  // Role-scope depth: derived from the effective anchor and preset.
  const roleAllowedKinds: readonly ScopeKind[] = useMemo(() => {
    if (presetScope) {
      // Preset locks the role scope exactly. Tabs collapse to the one
      // valid kind so the picker disappears.
      return [presetScope.scope_kind] as const;
    }
    if (tier === "platform") return ["partner"] as const;
    if (effectiveAnchor.kind === "partner") {
      return ["partner", "organization", "location"] as const;
    }
    if (effectiveAnchor.kind === "organization") return ["organization", "location"] as const;
    return ["partner", "organization", "location"] as const;
  }, [presetScope, tier, effectiveAnchor.kind]);

  // Reset downstream state when upstream choices change so a stale
  // selection from the previous shape never leaks through.
  useEffect(() => {
    if (presetScope) return;
    setAnchor(EMPTY_WORKPLACE);
    setRoleScope(EMPTY_WORKPLACE);
    setRoleSlug("");
  }, [pool, tier, presetScope]);

  useEffect(() => {
    if (presetScope) return;
    setRoleScope(EMPTY_WORKPLACE);
    setRoleSlug("");
  }, [anchor.id, presetScope]);

  // When the preset is set, force the role scope to match the preset
  // automatically so the operator only picks pool + profile + role.
  useEffect(() => {
    if (!presetScope) return;
    setRoleScope({
      kind: presetScope.scope_kind,
      id: presetScope.scope_id,
      labels: {
        partner: presetScope.scope_kind === "partner" ? presetScope.label : null,
        organization: presetScope.scope_kind === "organization" ? presetScope.label : null,
        location: presetScope.scope_kind === "location" ? presetScope.label : null,
      },
    });
  }, [presetScope]);

  // Platform tier always grants super_admin at the platform partner.
  useEffect(() => {
    if (presetScope) return;
    if (tier !== "platform") return;
    setPool("admin");
    setGrantRole(true);
    setRoleSlug("super_admin");
  }, [tier, presetScope]);

  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim().toLowerCase());

  const mutate = useMutation({
    mutationFn: async () => {
      if (!emailValid) throw new Error("Enter a valid email address.");
      if (!effectiveAnchor.id || !effectiveAnchor.kind) {
        if (tier === "platform" && !platformPartner) {
          throw new Error("Resolving the platform partner - try again in a moment.");
        }
        throw new Error(
          pool === "admin" ? "Pick a primary partner." : "Pick where this POS user works.",
        );
      }

      if (pool === "admin") {
        if (effectiveAnchor.kind !== "partner") {
          throw new Error("Admin users must have a partner as their primary workplace.");
        }
        // Platform tier: the API expects a super_admin grant at the
        // platform partner. Other tiers: build initial_role from the
        // role-scope picker (or the preset).
        const initialRole = (() => {
          if (tier === "platform") {
            return { partner_id: effectiveAnchor.id, role: "super_admin" as UserRole };
          }
          if (!grantRole || !roleSlug) return undefined;
          const rs = presetScope
            ? {
                kind: presetScope.scope_kind,
                id: presetScope.scope_id,
                partner_id: presetScope.partner_id,
              }
            : { kind: roleScope.kind, id: roleScope.id, partner_id: effectiveAnchor.id };
          if (!rs.id || !rs.kind) return undefined;
          if (rs.kind === "partner") {
            return { partner_id: rs.partner_id, role: roleSlug as UserRole };
          }
          return {
            scope_kind: rs.kind,
            scope_id: rs.id,
            role: roleSlug as UserRole,
          };
        })();
        return apiClient.inviteUser({
          email: email.trim().toLowerCase(),
          primary_partner_id: effectiveAnchor.id,
          ...(firstName ? { first_name: firstName } : {}),
          ...(lastName ? { last_name: lastName } : {}),
          ...(initialRole ? { initial_role: initialRole } : {}),
        });
      }

      // POS pool - anchor + optional initial_membership at any
      // sub-scope inside the anchor's subtree.
      if (effectiveAnchor.kind !== "partner" && effectiveAnchor.kind !== "organization") {
        throw new Error("POS users must be anchored at a partner or organization.");
      }
      const initialMembership = (() => {
        if (!grantRole || !roleSlug) return undefined;
        const rs = presetScope
          ? { kind: presetScope.scope_kind, id: presetScope.scope_id }
          : { kind: roleScope.kind, id: roleScope.id };
        if (!rs.id || !rs.kind) return undefined;
        return {
          scope_kind: rs.kind,
          scope_id: rs.id,
          role_slug: roleSlug,
        };
      })();
      return apiClient.createPosUser({
        email: email.trim().toLowerCase(),
        anchor_kind: effectiveAnchor.kind as PosAnchorKind,
        anchor_id: effectiveAnchor.id,
        ...(firstName ? { first_name: firstName } : {}),
        ...(lastName ? { last_name: lastName } : {}),
        ...(initialMembership ? { initial_membership: initialMembership } : {}),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["people"] });
      void qc.invalidateQueries({ queryKey: ["scoped-people"] });
      onCreated();
    },
  });

  const submitDisabled =
    !emailValid ||
    !effectiveAnchor.id ||
    (pool === "admin" && effectiveAnchor.kind !== "partner") ||
    (pool === "pos" &&
      effectiveAnchor.kind !== "partner" &&
      effectiveAnchor.kind !== "organization") ||
    (grantRole && !presetScope && tier !== "platform" && (!roleScope.id || !roleSlug)) ||
    (grantRole && presetScope && !roleSlug) ||
    (tier === "platform" && !platformPartner);

  const poolLabel = pool === "admin" ? "admin console" : "POS application";

  // POS pool can't anchor at platform; flip the tier for super-admins
  // who switch pools mid-flow. A noop when the toggle isn't shown.
  useEffect(() => {
    if (pool === "pos" && tier === "platform") setTier("partner");
  }, [pool, tier]);

  // Wizard step machine - three named steps. Step 2 (workplace) is
  // skipped when there's nothing to pick (preset or platform tier).
  // Operator can go back to a prior step at any time; "Continue"
  // advances only when the current step's invariants hold.
  type Step = "who" | "where" | "role";
  const skipWhere = !!presetScope || tier === "platform";
  const [step, setStep] = useState<Step>("who");

  // If the user picks platform-tier or a preset is in play, fast-
  // forward past Where (it's a frozen badge, not interactive).
  useEffect(() => {
    if (skipWhere && step === "where") setStep("role");
  }, [skipWhere, step]);

  const whoComplete = emailValid;
  const whereComplete =
    skipWhere ||
    (effectiveAnchor.id !== "" &&
      ((pool === "admin" && effectiveAnchor.kind === "partner") ||
        (pool === "pos" &&
          (effectiveAnchor.kind === "partner" || effectiveAnchor.kind === "organization"))));
  const roleComplete =
    !grantRole || tier === "platform" || !!presetScope || (!!roleScope.id && !!roleSlug);

  const goNext = () => {
    if (step === "who" && whoComplete) {
      setStep(skipWhere ? "role" : "where");
    } else if (step === "where" && whereComplete) {
      setStep("role");
    }
  };
  const goBack = () => {
    if (step === "role") {
      setStep(skipWhere ? "who" : "where");
    } else if (step === "where") {
      setStep("who");
    }
  };

  return (
    <ModalOverlay ariaLabel="Add a person" onClose={onClose}>
      <div className="w-full max-w-lg space-y-4 rounded-lg bg-white p-6 shadow-xl">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Add a person</h3>
          <p className="mt-1 text-xs text-slate-500">
            Cognito sends the invite email. They'll set a password on first sign-in and land at the{" "}
            {poolLabel}.
          </p>
        </div>

        <WizardSteps
          current={step}
          skipWhere={skipWhere}
          whoComplete={whoComplete}
          whereComplete={whereComplete}
        />

        {step === "who" && (
          <>
            <fieldset className="space-y-2">
              <legend className="text-xs font-semibold uppercase text-slate-500">
                Which application?
              </legend>
              <div className="flex gap-3 text-sm">
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    checked={pool === "admin"}
                    onChange={() => setPool("admin")}
                  />
                  Admin console
                </label>
                <label className="flex items-center gap-1">
                  <input type="radio" checked={pool === "pos"} onChange={() => setPool("pos")} />
                  POS application
                </label>
              </div>
            </fieldset>

            {/* Tier toggle - surfaces ONLY when there's no preset (preset
            already nails the workplace down) and the caller is a
            super_admin and the pool is admin (POS doesn't have a
            platform tier - POS users always anchor inside a real
            partner or organization). */}
            {!presetScope && callerIsSuperAdmin && pool === "admin" && (
              <fieldset className="space-y-2">
                <legend className="text-xs font-semibold uppercase text-slate-500">Tier</legend>
                <div className="flex gap-3 text-sm">
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      checked={tier === "partner"}
                      onChange={() => setTier("partner")}
                    />
                    Partner-tier
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      checked={tier === "platform"}
                      onChange={() => setTier("platform")}
                    />
                    Platform-tier
                  </label>
                </div>
                <p className="text-xs text-slate-500">
                  {tier === "platform"
                    ? "Platform-tier people reach every partner and bypass the workplace pickers. Use sparingly."
                    : "Partner-tier people work inside one partner subtree - anyone from a partner-wide admin to a single-location cashier."}
                </p>
              </fieldset>
            )}

            <fieldset className="space-y-2">
              <legend className="text-xs font-semibold uppercase text-slate-500">Profile</legend>
              <input
                type="email"
                aria-label="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                className={cn(
                  "block w-full rounded border px-2 py-1.5 text-sm",
                  email && !emailValid ? "border-red-300" : "border-slate-300",
                )}
              />
              <div className="flex gap-2">
                <input
                  aria-label="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                  className="block w-1/2 rounded border border-slate-300 px-2 py-1.5 text-sm"
                />
                <input
                  aria-label="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                  className="block w-1/2 rounded border border-slate-300 px-2 py-1.5 text-sm"
                />
              </div>
            </fieldset>
          </>
        )}

        {step === "where" && (
          <>
            {/* Workplace section - three modes:
            (a) preset → frozen badge, no picker
            (b) platform tier → "Platform" badge, no picker
            (c) default → live picker
        */}
            {presetScope ? (
              <FrozenWorkplaceBadge
                label={presetScope.label}
                scopeKind={presetScope.scope_kind}
                note={
                  pool === "admin"
                    ? "New admin's primary partner + initial role are locked to this workplace."
                    : `New POS user is anchored at the ${
                        presetScope.scope_kind === "partner"
                          ? "partner"
                          : presetScope.scope_kind === "organization"
                            ? "organization"
                            : "parent organization"
                      } and granted a role at this ${presetScope.scope_kind}.`
                }
              />
            ) : tier === "platform" ? (
              <FrozenWorkplaceBadge
                label="Platform"
                scopeKind="partner"
                note="The platform root is not a regular partner - it spans every customer. Only super_admins can hold a role here."
              />
            ) : (
              <fieldset className="space-y-2">
                <legend className="text-xs font-semibold uppercase text-slate-500">
                  {pool === "admin" ? "Primary workplace" : "Where will they work?"}
                </legend>
                <p className="text-xs text-slate-500">
                  {pool === "admin"
                    ? "Pick the partner this admin belongs to. They can hold roles anywhere under it."
                    : "Pick the partner OR organization that owns this POS account. They can hold roles anywhere under it."}
                </p>
                <WorkplacePicker
                  value={anchor}
                  onChange={setAnchor}
                  allowedKinds={anchorAllowedKinds}
                  idPrefix="invite-anchor"
                />
              </fieldset>
            )}
          </>
        )}

        {step === "role" && (
          <>
            {/* Initial role section - three modes:
            (a) preset → role-only (workplace already locked)
            (b) platform tier → role pinned to super_admin, no picker
            (c) default → role-scope picker + role select
        */}
            {tier === "platform" && !presetScope ? (
              <fieldset className="space-y-2">
                <legend className="text-xs font-semibold uppercase text-slate-500">
                  Initial role
                </legend>
                <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                  <span className="font-medium">super_admin</span> at the platform root.
                </div>
              </fieldset>
            ) : (
              <fieldset className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={grantRole}
                    onChange={(e) => setGrantRole(e.target.checked)}
                  />
                  Grant an initial role
                </label>
                {grantRole && (
                  <div className="space-y-3 rounded border border-slate-200 bg-slate-50 p-3">
                    {presetScope ? (
                      <p className="text-xs text-slate-600">
                        Pick the role to grant at <strong>{presetScope.label}</strong>. The list
                        filters to roles valid at a {presetScope.scope_kind}.
                      </p>
                    ) : (
                      <>
                        <p className="text-xs text-slate-600">
                          Pick where this role applies (must be inside the workplace above) and the
                          role itself. The role list filters to what's valid for the depth you pick
                          - so a Cashier role is only available at a location, a Partner Admin only
                          at a partner, etc.
                        </p>
                        {/* Pin the role-scope picker inside the workplace
                        chosen above. Partner-anchor → bind partner;
                        org-anchor → bind organization. The role-scope
                        picker keeps its depth tabs (Partner /
                        Organization / One location) but the partner
                        and/or org rows render as read-only badges so
                        the operator can't pick a sibling partner. */}
                        <WorkplacePicker
                          value={roleScope}
                          onChange={setRoleScope}
                          allowedKinds={roleAllowedKinds}
                          {...(effectiveAnchor.kind === "organization" && effectiveAnchor.id
                            ? {
                                boundOrganizationId: effectiveAnchor.id,
                                ...(effectiveAnchor.labels.organization
                                  ? { boundOrganizationName: effectiveAnchor.labels.organization }
                                  : {}),
                              }
                            : effectiveAnchor.kind === "partner" && effectiveAnchor.id
                              ? {
                                  boundPartnerId: effectiveAnchor.id,
                                  ...(effectiveAnchor.labels.partner
                                    ? { boundPartnerName: effectiveAnchor.labels.partner }
                                    : {}),
                                }
                              : {})}
                          idPrefix="invite-role-scope"
                        />
                      </>
                    )}
                    {/* Always hide super_admin from this picker - it's a
                    platform-tier role reachable only via the Tier =
                    Platform branch above (which doesn't render this
                    picker at all). Showing it here let operators
                    accidentally try to grant super_admin at a partner
                    scope, which the API rejects but is confusing UX. */}
                    <RoleSelect
                      pool={pool}
                      scopeKind={presetScope ? presetScope.scope_kind : roleScope.kind}
                      value={roleSlug}
                      onChange={setRoleSlug}
                      id="invite-role"
                      hideSuperAdmin
                    />
                  </div>
                )}
              </fieldset>
            )}
          </>
        )}

        {mutate.isError && (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {describe(mutate.error)}
          </div>
        )}

        {/* Wizard navigation. Step 1+2 show Continue (advances on
            valid); step 3 shows Send invite (mutates). Cancel always
            available. Back button only on steps after step 1. */}
        <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <div className="flex gap-2">
            {step !== "who" && (
              <button
                type="button"
                onClick={goBack}
                className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
              >
                ← Back
              </button>
            )}
            {step !== "role" ? (
              <button
                type="button"
                disabled={(step === "who" && !whoComplete) || (step === "where" && !whereComplete)}
                onClick={goNext}
                className="rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
              >
                Continue →
              </button>
            ) : (
              <button
                type="button"
                disabled={mutate.isPending || !roleComplete || submitDisabled}
                onClick={() => mutate.mutate()}
                className="rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
              >
                {mutate.isPending ? "Inviting…" : "Send invite"}
              </button>
            )}
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
}

/** Wizard step indicator - the three dots at the top so the
 *  operator knows where they are. Disabled / muted style on a
 *  step that's not yet reachable; the active step gets the brand
 *  highlight; completed steps render a green checkmark. */
function WizardSteps({
  current,
  skipWhere,
  whoComplete,
  whereComplete,
}: {
  readonly current: "who" | "where" | "role";
  readonly skipWhere: boolean;
  readonly whoComplete: boolean;
  readonly whereComplete: boolean;
}) {
  const steps: Array<{ key: "who" | "where" | "role"; label: string }> = skipWhere
    ? [
        { key: "who", label: "Who?" },
        { key: "role", label: "Role" },
      ]
    : [
        { key: "who", label: "Who?" },
        { key: "where", label: "Where?" },
        { key: "role", label: "Role" },
      ];
  const isComplete = (k: "who" | "where" | "role") =>
    (k === "who" && whoComplete && current !== "who") ||
    (k === "where" && whereComplete && current === "role");
  return (
    <ol className="flex items-center gap-2 text-xs">
      {steps.map((s, i) => {
        const active = current === s.key;
        const done = isComplete(s.key);
        return (
          <li key={s.key} className="flex items-center gap-2">
            <span
              className={cn(
                "flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold",
                active && "bg-slate-900 text-white",
                done && "bg-emerald-500 text-white",
                !active && !done && "bg-slate-200 text-slate-500",
              )}
            >
              {done ? "✓" : i + 1}
            </span>
            <span className={cn("font-medium", active ? "text-slate-900" : "text-slate-500")}>
              {s.label}
            </span>
            {i < steps.length - 1 && (
              <span aria-hidden="true" className="mx-1 h-px w-6 bg-slate-200" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function FrozenWorkplaceBadge({
  label,
  scopeKind,
  note,
}: {
  readonly label: string;
  readonly scopeKind: ScopeKind;
  readonly note: string;
}) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-xs font-semibold uppercase text-slate-500">Workplace</legend>
      <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
        <span className="font-medium text-slate-800">{label}</span>
        <span className="text-[10px] uppercase tracking-wide text-slate-400">{scopeKind}</span>
      </div>
      <p className="text-xs text-slate-500">{note}</p>
    </fieldset>
  );
}
